/** Ключ пресета быстрого откладывания (для маппинга иконки/аналитики). */
export type SnoozePresetKey = 'in30m' | 'in1h' | 'evening' | 'tomorrow' | 'nextWeek';

/** Пресет быстрого откладывания уведомления. */
export interface SnoozePreset {
    key: SnoozePresetKey;
    /** Подпись пункта меню, напр. «Через 30 минут». */
    label: string;
    /** Человекочитаемое целевое время, напр. «14:35» или «завтра, 9:00». */
    when: string;
    /** Точная дата/время, на которое создаётся напоминание. */
    date: Date;
}

const WD = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
const MON = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const pad = (n: number) => String(n).padStart(2, '0');
const hm = (d: Date) => `${d.getHours()}:${pad(d.getMinutes())}`;

/**
 * Пресеты быстрого откладывания, посчитанные от `now`:
 * через 30 минут, через час, сегодня вечером (18:00, только до 17:00),
 * завтра (9:00), через неделю (+7 дней, 9:00).
 *
 * Чистая функция — переиспользуется превью-тостом, дропдауном и карточкой уведомления.
 */
export function buildSnoozePresets(now: Date = new Date()): SnoozePreset[] {
    const presets: SnoozePreset[] = [];
    const at = (ms: number) => new Date(now.getTime() + ms);

    const in30 = at(30 * 60_000);
    presets.push({ key: 'in30m', label: 'Через 30 минут', when: hm(in30), date: in30 });

    const in1h = at(60 * 60_000);
    presets.push({ key: 'in1h', label: 'Через час', when: hm(in1h), date: in1h });

    if (now.getHours() < 17) {
        const ev = new Date(now);
        ev.setHours(18, 0, 0, 0);
        presets.push({ key: 'evening', label: 'Сегодня вечером', when: hm(ev), date: ev });
    }

    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    presets.push({ key: 'tomorrow', label: 'Завтра', when: `завтра, ${hm(tomorrow)}`, date: tomorrow });

    const week = new Date(now);
    week.setDate(week.getDate() + 7);
    week.setHours(9, 0, 0, 0);
    presets.push({
        key: 'nextWeek',
        label: 'Через неделю',
        when: `${WD[week.getDay()]}, ${week.getDate()} ${MON[week.getMonth()]}`,
        date: week,
    });

    return presets;
}

/**
 * Дружелюбная подпись цели напоминания для тоста: «сегодня в 14:35» / «завтра в 9:00» / «7 июл в 9:00».
 * Общая для откладывания уведомлений и quick-action «отложить» в виджете «Календарь».
 */
export function formatSnoozeTarget(date: Date, now: Date = new Date()): string {
    const time = hm(date);
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    if (date.toDateString() === now.toDateString()) return `сегодня в ${time}`;
    if (date.toDateString() === tomorrow.toDateString()) return `завтра в ${time}`;
    return `${date.getDate()} ${MON[date.getMonth()]} в ${time}`;
}
